import React, { useState, useEffect } from 'react';
import { api } from '../../hooks/useApi';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Upload, Loader2, CheckCircle2, FileSpreadsheet } from 'lucide-react';
import { apiError } from '../sales/salesShared';

// Two steps: 1) upload -> the server reports what WOULD happen (nothing is
// saved), 2) confirm -> the same file is imported.
const ImportClientsModal = ({ open, onClose, onImported, clientsLabel = 'Clients' }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Also give rows that are already clients the rep named in the file.
  const [updateReps, setUpdateReps] = useState(false);
  // Also set the contract end date of rows that are already clients.
  const [updateContracts, setUpdateContracts] = useState(false);

  useEffect(() => {
    if (open) { setFile(null); setPreview(null); setResult(null); setError(''); setBusy(false); setUpdateReps(false); setUpdateContracts(false); }
  }, [open]);

  const run = async (dryRun, chosen, withUpdates = updateReps, withContracts = updateContracts) => {
    const f = chosen || file;
    if (!f) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.upload(`/advertisers/import?dry_run=${dryRun ? 'true' : 'false'}&update_reps=${withUpdates ? 'true' : 'false'}&update_contracts=${withContracts ? 'true' : 'false'}`, f);
      if (dryRun) setPreview(res);
      else { setResult(res); if (onImported) onImported(); }
    } catch (e) {
      setError(apiError(e, 'Import failed'));
    } finally {
      setBusy(false);
    }
  };

  const pick = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
    setPreview(null);
    if (f) run(true, f);
  };

  const toggleUpdates = (e) => {
    const on = e.target.checked;
    setUpdateReps(on);
    if (file) run(true, file, on);
  };

  const toggleContracts = (e) => {
    const on = e.target.checked;
    setUpdateContracts(on);
    if (file) run(true, file, updateReps, on);
  };

  const confirmLabel = () => {
    const parts = [];
    if (preview.to_import) parts.push(`import ${Number(preview.to_import).toLocaleString()} ${clientsLabel.toLowerCase()}`);
    if (preview.reps_to_update) parts.push(`update ${Number(preview.reps_to_update).toLocaleString()} rep${preview.reps_to_update === 1 ? '' : 's'}`);
    if (preview.contracts_to_update) parts.push(`set ${Number(preview.contracts_to_update).toLocaleString()} contract date${preview.contracts_to_update === 1 ? '' : 's'}`);
    const text = parts.join(' and ');
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Nothing to do';
  };

  const Stat = ({ label, value, testid, tone = 'text-[#F8FAFC]' }) => (
    <div className="bg-[#0A0A0A] border border-[#1E293B] rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wide text-[#94A3B8]">{label}</div>
      <div className={`text-xl font-bold font-mono ${tone}`} data-testid={testid}>{Number(value || 0).toLocaleString()}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="import-clients-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#3B82F6]" /> Import {clientsLabel}</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            Excel (.xlsx) or CSV with a &quot;Business Name&quot; column. Other columns it understands: Industry, Contact Person,
            Phone, Alt Phone, Email, Website, Address, Postal Code, Location Type, Sales Rep, Rep Note, Docket Numbers, Contract End, Source.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <label className="flex items-center gap-3 border border-dashed border-[#1E293B] rounded-md p-4 cursor-pointer hover:border-[#3B82F6]">
            <Upload className="w-5 h-5 text-[#94A3B8]" />
            <span className="text-sm text-[#94A3B8] flex-1 truncate">{file ? file.name : 'Choose a file…'}</span>
            <input type="file" accept=".xlsx,.csv" onChange={pick} className="hidden" data-testid="import-file-input" />
            {busy && <Loader2 className="w-4 h-4 animate-spin text-[#3B82F6]" />}
          </label>
        )}

        {error && <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2" data-testid="import-error">{error}</div>}

        {preview && !result && (
          <div className="space-y-4" data-testid="import-preview">
            <p className="text-sm text-[#94A3B8]">Nothing has been saved yet. This is what the import will do:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Rows in file" value={preview.rows_in_file} testid="import-rows" />
              <Stat label="Will be added" value={preview.to_import} testid="import-to-add" tone="text-emerald-400" />
              <Stat label="Already here (skipped)" value={preview.duplicates} testid="import-duplicates" tone="text-amber-400" />
              <Stat label="Left unassigned" value={preview.unassigned} testid="import-unassigned" />
            </div>
            <div className="text-sm text-[#94A3B8] space-y-1">
              {Object.keys(preview.assigned_by_rep || {}).length > 0 && (
                <p>Assigned by the Sales Rep column: {Object.entries(preview.assigned_by_rep).map(([n, c]) => `${n} ${c}`).join(', ')}.</p>
              )}
              {preview.new_sales_reps?.length > 0 && (
                <p className="text-amber-300" data-testid="import-new-reps">
                  New sales reps will be created: {preview.new_sales_reps.join(', ')}. Add their email in Settings → Sales Reps so their logins match.
                </p>
              )}
              {preview.new_industries?.length > 0 && <p>{preview.new_industries.length} new industr{preview.new_industries.length === 1 ? 'y' : 'ies'} will be added to the list.</p>}
              {preview.duplicate_examples?.length > 0 && (
                <p className="text-xs text-[#475569]">Skipped, for example: {preview.duplicate_examples.slice(0, 6).join('; ')}</p>
              )}
            </div>
            <label className="flex items-start gap-2 text-sm text-[#F8FAFC] bg-[#0A0A0A] border border-[#1E293B] rounded-md p-3 cursor-pointer" data-testid="import-update-reps">
              <input type="checkbox" checked={updateReps} onChange={toggleUpdates} disabled={busy} className="accent-[#3B82F6] mt-0.5" data-testid="import-update-reps-checkbox" />
              <span>
                Also update the sales rep of rows that are already {clientsLabel.toLowerCase()}
                <span className="block text-xs text-[#94A3B8] mt-0.5">
                  {updateReps
                    ? `${Number(preview.reps_to_update || 0).toLocaleString()} of the ${Number(preview.already_clients || 0).toLocaleString()} already here will get the rep named in the file${Object.keys(preview.rep_updates_by_rep || {}).length ? ` (${Object.entries(preview.rep_updates_by_rep).map(([n, c]) => `${n} ${c}`).join(', ')})` : ''}. Rows with no rep leave the client as it is.`
                    : `${Number(preview.already_clients || 0).toLocaleString()} rows are already ${clientsLabel.toLowerCase()}. Tick this to give them the rep named in the file.`}
                  {preview.ambiguous > 0 && ` ${preview.ambiguous} row(s) match more than one client and are left alone.`}
                </span>
              </span>
            </label>
            {preview.has_contract_column && (
              <label className="flex items-start gap-2 text-sm text-[#F8FAFC] bg-[#0A0A0A] border border-[#1E293B] rounded-md p-3 cursor-pointer" data-testid="import-update-contracts">
                <input type="checkbox" checked={updateContracts} onChange={toggleContracts} disabled={busy} className="accent-[#3B82F6] mt-0.5" data-testid="import-update-contracts-checkbox" />
                <span>
                  Also set the contract end date of rows that are already {clientsLabel.toLowerCase()}
                  <span className="block text-xs text-[#94A3B8] mt-0.5">
                    {updateContracts
                      ? `${Number(preview.contracts_to_update || 0).toLocaleString()} will get the date in the file. Rows with no date leave the client as it is.`
                      : 'Tick this to load the dates onto clients you already have.'}
                  </span>
                </span>
              </label>
            )}
            {preview.bad_contract_dates > 0 && (
              <p className="text-xs text-amber-400" data-testid="import-bad-contracts">
                {preview.bad_contract_dates} contract date(s) could not be read and will be skipped, for example: {preview.bad_contract_examples.slice(0, 5).join('; ')}. Use a format like 2027-03-31 or 31-Mar-2027.
              </p>
            )}
            {preview.sample?.length > 0 && (
              <div className="border border-[#1E293B] rounded-md overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#0A0A0A] text-[#94A3B8] uppercase">
                    <tr><th className="text-left px-2 py-1.5">Business</th><th className="text-left px-2 py-1.5">Industry</th><th className="text-left px-2 py-1.5">Phone</th><th className="text-left px-2 py-1.5">Sales rep</th></tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((r, i) => (
                      <tr key={`${r.business_name}-${i}`} className="border-t border-[#1E293B]">
                        <td className="px-2 py-1.5 text-[#F8FAFC]">{r.business_name}</td>
                        <td className="px-2 py-1.5 text-[#94A3B8]">{r.industry || ''}</td>
                        <td className="px-2 py-1.5 text-[#94A3B8]">{r.phone || ''}</td>
                        <td className="px-2 py-1.5 text-[#94A3B8]">{r.sales_rep || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={onClose} disabled={busy} className="border-[#1E293B] bg-[#121212] text-[#94A3B8]">Cancel</Button>
              <Button type="button" onClick={() => run(false)} disabled={busy || !(preview.to_import || preview.reps_to_update || preview.contracts_to_update)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="import-confirm-btn">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : confirmLabel()}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="import-result">
            <div className="flex items-center gap-2 text-sm text-emerald-400 bg-emerald-900/20 border border-emerald-800 rounded-md p-3">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span data-testid="import-done-text">
                Imported {Number(result.imported).toLocaleString()} {clientsLabel.toLowerCase()}
                {result.duplicates ? `, skipped ${Number(result.duplicates).toLocaleString()} already here` : ''}
                {result.reps_updated ? `, updated the rep of ${Number(result.reps_updated).toLocaleString()}` : ''}.
              </span>
            </div>
            <div className="flex justify-end">
              <Button type="button" onClick={onClose} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="import-close-btn">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportClientsModal;
